import React, {useEffect, useState} from 'react'
import {
  getConfigurationByName,
  updateConfigurationByConfigID,
  uploadMedia,
} from '../../../../../../API/api-endpoint'
import ToastUtils, {ErrorToastUtils} from '../../../../../../utils/ToastUtils'
import {getImageBaseUrl} from '../../../../../../utils/Utils'
import {Modal, Button, Table, Form} from 'react-bootstrap'
import {request} from '../../../../../../middleware/auth-intercepter'
import {API_TYPE} from '../../../../../../utils/Utils'

interface Field {
  label: string;
  key: string;
  type: 'text' | 'number' | 'select';
  options?: string[];
  optionsRaw?: string;
}

interface PaymentMethod {
  id: string;
  name: string;
  icon: string;
  showVerify: boolean;
  fields: Field[];
  status: boolean;
}

const CustomPaymentMethodsPlugin = () => {
  const [configID, setConfigId] = useState<number | null>(null)
  const [paymentMethods, setPaymentMethods] = useState<PaymentMethod[]>([])
  const [loading, setLoading] = useState(false)
  const [showModal, setShowModal] = useState(false)
  const [currentMethod, setCurrentMethod] = useState<PaymentMethod>({
    id: '',
    name: '',
    icon: '',
    showVerify: false,
    fields: [],
    status: true,
  })
  const [editingIndex, setEditingIndex] = useState<number | null>(null)

  useEffect(() => {
    fetchPaymentMethods()
  }, [])

  const fetchPaymentMethods = async () => {
    setLoading(true)
    const result = await getConfigurationByName('custom_payment_methods')
    if (result?.success && result?.data) {
      const parsedData = JSON.parse(result.data.values)
      setConfigId(result.data.id)
      setPaymentMethods(parsedData || [])
    } else {
      setPaymentMethods([])
    }
    setLoading(false)
  }

  const handleSaveList = async (newList: PaymentMethod[]) => {
    if (configID === null) {
      // Create new config row in database
      const response = await request(
        '/masters/configurations/create',
        API_TYPE.POST,
        {
          type: 'custom_payment_methods',
          category: 'payment',
          status: true,
          values: JSON.stringify(newList),
        },
        true,
        false
      )
      if (response?.success) {
        ToastUtils({type: 'success', message: 'Payment methods saved successfully'})
        fetchPaymentMethods()
      } else {
        ErrorToastUtils()
      }
    } else {
      const result = await updateConfigurationByConfigID(configID, newList, null)
      if (result.success) {
        ToastUtils({type: 'success', message: 'Payment methods saved successfully'})
        fetchPaymentMethods()
      } else {
        ErrorToastUtils()
      }
    }
  }

  const handleFileUpload = async (event: any) => {
    const file = event.target.files[0]
    if (!file) return
    setLoading(true)
    const result = await uploadMedia(file)
    setLoading(false)
    if (result.success) {
      const path = result.data.path
      setCurrentMethod({...currentMethod, icon: path})
      ToastUtils({type: 'success', message: 'Icon uploaded successfully'})
    } else {
      ErrorToastUtils()
    }
  }

  const openAddModal = () => {
    setCurrentMethod({
      id: Date.now().toString(),
      name: '',
      icon: '',
      showVerify: false,
      fields: [],
      status: true,
    })
    setEditingIndex(null)
    setShowModal(true)
  }

  const openEditModal = (index: number) => {
    setCurrentMethod({
      ...paymentMethods[index],
      fields: paymentMethods[index].fields ? [...paymentMethods[index].fields] : [],
    })
    setEditingIndex(index)
    setShowModal(true)
  }

  const savePaymentMethod = () => {
    if (!currentMethod.name) {
      ToastUtils({type: 'error', message: 'Name is required'})
      return
    }

    // Validate fields inside currentMethod
    for (const f of currentMethod.fields) {
      if (!f.label.trim()) {
        ToastUtils({type: 'error', message: 'All fields must have a label'})
        return
      }
      if (!f.key.trim()) {
        ToastUtils({type: 'error', message: 'All fields must have a key'})
        return
      }
    }

    // Clean optionsRaw before saving to DB
    const cleanedFields = currentMethod.fields.map((f) => {
      const {optionsRaw, ...rest} = f
      return rest
    })
    const cleanedMethod = {...currentMethod, fields: cleanedFields}

    const newList = [...paymentMethods]
    if (editingIndex !== null) {
      newList[editingIndex] = cleanedMethod
    } else {
      newList.push(cleanedMethod)
    }
    setPaymentMethods(newList)
    handleSaveList(newList)
    setShowModal(false)
  }

  const deletePaymentMethod = (index: number) => {
    if (window.confirm('Are you sure you want to delete this payment method?')) {
      const newList = paymentMethods.filter((_, i) => i !== index)
      setPaymentMethods(newList)
      handleSaveList(newList)
    }
  }

  const toggleStatus = (index: number) => {
    const newList = [...paymentMethods]
    newList[index].status = !newList[index].status
    setPaymentMethods(newList)
    handleSaveList(newList)
  }

  const addField = () => {
    const updatedFields = [...currentMethod.fields, {label: '', key: '', type: 'text' as const}]
    setCurrentMethod({...currentMethod, fields: updatedFields})
  }

  const removeField = (fIdx: number) => {
    const updatedFields = currentMethod.fields.filter((_, idx) => idx !== fIdx)
    setCurrentMethod({...currentMethod, fields: updatedFields})
  }

  const updateField = (fIdx: number, key: keyof Field, value: any) => {
    const updatedFields = [...currentMethod.fields]
    updatedFields[fIdx] = {
      ...updatedFields[fIdx],
      [key]: value,
    } as any
    if (key === 'label') {
      updatedFields[fIdx].key = value
        .toLowerCase()
        .replace(/[^a-z0-9_]+/g, '_')
        .replace(/^_+|_+$/g, '')
    }
    setCurrentMethod({...currentMethod, fields: updatedFields})
  }

  const updateFieldOptions = (fIdx: number, valueStr: string) => {
    const updatedFields = [...currentMethod.fields]
    const options = valueStr.split(',').map(item => item.trim()).filter(Boolean)
    updatedFields[fIdx] = {
      ...updatedFields[fIdx],
      options: options,
      optionsRaw: valueStr,
    }
    setCurrentMethod({...currentMethod, fields: updatedFields})
  }

  return (
    <div className='card'>
      <div className='card-header pt-7 border-0'>
        <h3 className='card-title align-items-start flex-column'>
          <span className='card-label fw-bold text-gray-800'>Withdrawal Payment Methods</span>
          <span className='text-gray-400 mt-1 fw-semibold fs-6'>Configure available withdrawal options and dynamic fields</span>
        </h3>
        <div className='card-toolbar'>
          <button className='btn btn-sm btn-primary' onClick={openAddModal}>
            Add Method
          </button>
        </div>
      </div>
      <div className='card-body pt-5'>
        <div className='table-responsive'>
          <Table className='table table-row-dashed table-row-gray-300 align-middle gs-0 gy-4'>
            <thead>
              <tr className='fw-bold text-muted bg-light'>
                <th className='ps-4 min-w-100px rounded-start'>Icon</th>
                <th className='min-w-150px'>Name</th>
                <th className='min-w-200px'>Fields Defined</th>
                <th className='min-w-100px text-center'>Show Verify</th>
                <th className='min-w-100px text-center'>Status</th>
                <th className='min-w-100px text-end rounded-end'>Actions</th>
              </tr>
            </thead>
            <tbody>
              {paymentMethods.map((item, index) => (
                <tr key={item.id || index}>
                  <td className='ps-4'>
                    {item.icon ? (
                      <div className='symbol symbol-45px'>
                        <img
                          src={item.icon.startsWith('http') ? item.icon : `${getImageBaseUrl()}/${item.icon}`}
                          alt={item.name}
                          style={{objectFit: 'contain'}}
                        />
                      </div>
                    ) : (
                      <div className='symbol symbol-45px bg-light rounded d-flex align-items-center justify-content-center'>
                        <i className='fa-solid fa-image text-muted'></i>
                      </div>
                    )}
                  </td>
                  <td>
                    <span className='text-gray-800 fw-bold d-block fs-6'>{item.name}</span>
                  </td>
                  <td>
                    <div className='d-flex flex-wrap gap-1'>
                      {item.fields && item.fields.length > 0 ? (
                        item.fields.map((field, fIdx) => (
                          <span key={fIdx} className='badge badge-light-primary fw-bold me-1 mb-1'>
                            {field.label} ({field.type === 'select' && field.options && field.options.length > 0 
                              ? `select: ${field.options.join('/')}` 
                              : field.type})
                          </span>
                        ))
                      ) : (
                        <span className='text-muted fs-7'>No fields defined</span>
                      )}
                    </div>
                  </td>
                  <td className='text-center'>
                    <span className={`badge badge-light-${item.showVerify ? 'success' : 'secondary'} fw-bold`}>
                      {item.showVerify ? 'Yes' : 'No'}
                    </span>
                  </td>
                  <td className='text-center'>
                    <div className='form-check form-switch d-flex justify-content-center'>
                      <input
                        className='form-check-input'
                        type='checkbox'
                        role='switch'
                        checked={item.status}
                        onChange={() => toggleStatus(index)}
                      />
                    </div>
                  </td>
                  <td className='text-end'>
                    <button
                      className='btn btn-icon btn-bg-light btn-active-color-primary btn-sm me-1'
                      onClick={() => openEditModal(index)}
                    >
                      <i className='fa-solid fa-pencil'></i>
                    </button>
                    <button
                      className='btn btn-icon btn-bg-light btn-active-color-danger btn-sm'
                      onClick={() => deletePaymentMethod(index)}
                    >
                      <i className='fa-solid fa-trash'></i>
                    </button>
                  </td>
                </tr>
              ))}
              {paymentMethods.length === 0 && !loading && (
                <tr>
                  <td colSpan={6} className='text-center text-muted py-10'>
                    No payment methods added yet. Click "Add Method" to start.
                  </td>
                </tr>
              )}
              {loading && (
                <tr>
                  <td colSpan={6} className='text-center text-muted py-10'>
                    Loading...
                  </td>
                </tr>
              )}
            </tbody>
          </Table>
        </div>
      </div>

      <Modal show={showModal} onHide={() => setShowModal(false)} centered size='lg'>
        <Modal.Header closeButton>
          <Modal.Title className='fw-bold'>
            {editingIndex !== null ? 'Edit Payment Method' : 'Add New Payment Method'}
          </Modal.Title>
        </Modal.Header>
        <Modal.Body className='px-10 py-8'>
          <Form>
            <Form.Group className='mb-5'>
              <Form.Label className='fw-bold fs-6'>Method Name</Form.Label>
              <Form.Control
                type='text'
                className='form-control-solid'
                value={currentMethod.name}
                onChange={(e) => setCurrentMethod({...currentMethod, name: e.target.value})}
                placeholder='e.g. Bank Transfer, UPI, PayPal'
              />
            </Form.Group>

            <Form.Group className='mb-5'>
              <Form.Label className='fw-bold fs-6 d-block'>Method Icon</Form.Label>
              <div className='d-flex align-items-center gap-5 mt-2'>
                <div className='symbol symbol-100px border border-dashed border-primary bg-light d-flex align-items-center justify-content-center overflow-hidden'>
                  {currentMethod.icon ? (
                    <img
                      src={currentMethod.icon.startsWith('http') ? currentMethod.icon : `${getImageBaseUrl()}/${currentMethod.icon}`}
                      alt='preview'
                      className='w-100 h-100'
                      style={{objectFit: 'contain'}}
                    />
                  ) : (
                    <i className='fa-solid fa-image fs-2x text-muted'></i>
                  )}
                </div>
                <div className='flex-grow-1'>
                  <Form.Control
                    type='file'
                    className='form-control-solid'
                    onChange={handleFileUpload}
                    accept='image/*'
                  />
                  <div className='text-muted fs-7 mt-1'>Allowed types: png, jpg, jpeg, webp.</div>
                </div>
              </div>
            </Form.Group>

            <div className='form-check form-switch mb-8'>
              <input
                className='form-check-input'
                type='checkbox'
                role='switch'
                id='showVerifyCheckbox'
                checked={currentMethod.showVerify}
                onChange={(e) => setCurrentMethod({...currentMethod, showVerify: e.target.checked})}
              />
              <Form.Label className='form-check-label fw-bold' htmlFor='showVerifyCheckbox'>
                Show Verify Button (Used for UPI verification)
              </Form.Label>
            </div>

            <div className='separator separator-dashed my-6'></div>

            <div>
              <div className='d-flex justify-content-between align-items-center mb-4'>
                <h4 className='fw-bold mb-0'>Form Fields Definition</h4>
                <button type='button' className='btn btn-sm btn-light-primary' onClick={addField}>
                  <i className='fa-solid fa-plus me-1'></i> Add Field
                </button>
              </div>

              {currentMethod.fields.map((field, fIdx) => (
                <React.Fragment key={fIdx}>
                  <div className='row align-items-center mb-3 g-3'>
                    <div className='col-md-5'>
                      <input
                        type='text'
                        placeholder='Field Label (e.g. Account number)'
                        className='form-control form-control-solid'
                        value={field.label}
                        onChange={(e) => updateField(fIdx, 'label', e.target.value)}
                      />
                    </div>
                    <div className='col-md-4'>
                      <input
                        type='text'
                        placeholder='Field Key (auto-generated)'
                        className='form-control form-control-solid bg-light'
                        value={field.key}
                        readOnly
                      />
                    </div>
                    <div className='col-md-2'>
                      <select
                        className='form-select form-select-solid'
                        value={field.type}
                        onChange={(e) => updateField(fIdx, 'type', e.target.value as any)}
                      >
                        <option value='text'>Text</option>
                        <option value='number'>Number</option>
                        <option value='select'>Select</option>
                      </select>
                    </div>
                    <div className='col-md-1 text-end'>
                      <button
                        type='button'
                        className='btn btn-icon btn-light-danger btn-sm'
                        onClick={() => removeField(fIdx)}
                      >
                        <i className='fa-solid fa-trash'></i>
                      </button>
                    </div>
                  </div>
                  {field.type === 'select' && (
                    <div className='row align-items-center mb-4 g-3 ps-5'>
                      <div className='col-md-12'>
                        <input
                          type='text'
                          placeholder='Enter select options separated by comma (e.g. Savings, Current)'
                          className='form-control form-control-solid bg-light-warning'
                          value={field.optionsRaw !== undefined ? field.optionsRaw : (field.options ? field.options.join(', ') : '')}
                          onChange={(e) => updateFieldOptions(fIdx, e.target.value)}
                        />
                        <div className='text-muted fs-8 mt-1'>
                          These items will appear as dropdown selection options for the user.
                        </div>
                      </div>
                    </div>
                  )}
                </React.Fragment>
              ))}
              {currentMethod.fields.length === 0 && (
                <div className='text-center py-4 bg-light rounded border border-dashed border-gray-300 text-muted fs-7'>
                  No fields defined. Define at least one input field for this payment method.
                </div>
              )}
            </div>
          </Form>
        </Modal.Body>
        <Modal.Footer className='border-0 pt-0 px-10 pb-8'>
          <Button variant='light' onClick={() => setShowModal(false)}>
            Discard
          </Button>
          <Button variant='primary' onClick={savePaymentMethod} disabled={loading}>
            {loading ? 'Saving...' : (editingIndex !== null ? 'Update Method' : 'Create Method')}
          </Button>
        </Modal.Footer>
      </Modal>
    </div>
  )
}

export default CustomPaymentMethodsPlugin
